import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { learningPathsService, trainingCatalogService } from '../../../services/enablementService';
import { partnerService } from '../../../services/partnerService';
import { useToast } from '../../../contexts/ToastContext';

const TIERS = ['Tactical', 'Silver', 'Gold', 'Strategic'];
const tierColors = {
  Tactical: { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-300', gradient: 'from-slate-500 to-slate-700' },
  Silver: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', gradient: 'from-blue-500 to-indigo-600' },
  Gold: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', gradient: 'from-amber-500 to-orange-600' },
  Strategic: { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200', gradient: 'from-violet-500 to-purple-700' },
};

const AddPathModal = ({ onClose, onSuccess, catalog }) => {
  const [form, setForm] = useState({ path_name: '', description: '', target_tier: 'Silver', estimated_duration: '' });
  const [selectedItems, setSelectedItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const handleChange = (e) => setForm(p => ({ ...p, [e?.target?.name]: e?.target?.value }));
  const toggleItem = (id) => setSelectedItems(p => p?.includes(id) ? p?.filter(x => x !== id) : [...p, id]);
  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.path_name?.trim()) { setError('Path name is required.'); return; }
    setSaving(true); setError(null);
    try {
      const path = await learningPathsService?.create({ ...form, path_status: 'active' });
      for (let i = 0; i < selectedItems?.length; i++) {
        await learningPathsService?.addItem(path?.id, selectedItems?.[i], i + 1, true);
      }
      onSuccess();
    } catch (err) { setError(err?.message || 'Failed to create learning path.'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-16">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-24 py-18 border-b border-slate-100 bg-gradient-to-r from-amber-50 to-orange-50 flex-shrink-0">
          <div className="flex items-center gap-12">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center"><Icon name="Route" size={18} className="text-white" /></div>
            <h2 className="text-base font-bold text-slate-900">Create Learning Path</h2>
          </div>
          <button onClick={onClose} className="p-8 rounded-lg hover:bg-slate-100 text-slate-500"><Icon name="X" size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-24 space-y-14 overflow-y-auto flex-1">
          {error && <div className="flex items-center gap-10 p-12 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm"><Icon name="AlertCircle" size={15} />{error}</div>}
          <div><label className="block text-xs font-semibold text-slate-600 mb-6">Path Name *</label><input name="path_name" value={form?.path_name} onChange={handleChange} placeholder="e.g. Gold Partner Excellence" className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/20" required /></div>
          <div><label className="block text-xs font-semibold text-slate-600 mb-6">Description</label><textarea name="description" value={form?.description} onChange={handleChange} rows={2} className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/20 resize-none" /></div>
          <div className="grid grid-cols-2 gap-14">
            <div><label className="block text-xs font-semibold text-slate-600 mb-6">Target Tier</label>
              <select name="target_tier" value={form?.target_tier} onChange={handleChange} className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/20 bg-white">
                {TIERS?.map(t => <option key={t} value={t}>{t}</option>)}
              </select></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-6">Duration</label><input name="estimated_duration" value={form?.estimated_duration} onChange={handleChange} placeholder="e.g. 6 weeks" className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/20" /></div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-10">Add Trainings ({selectedItems?.length} selected)</label>
            <div className="space-y-8 max-h-48 overflow-y-auto border border-slate-200 rounded-xl p-12">
              {catalog?.map(item => (
                <label key={item?.id} className="flex items-center gap-10 cursor-pointer hover:bg-slate-50 p-8 rounded-lg">
                  <input type="checkbox" checked={selectedItems?.includes(item?.id)} onChange={() => toggleItem(item?.id)} className="w-4 h-4 rounded border-slate-300 text-amber-600" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{item?.title}</p>
                    <p className="text-xs text-slate-500">{item?.category} · {item?.duration}</p>
                  </div>
                </label>
              ))}
              {catalog?.length === 0 && <p className="text-xs text-slate-400 text-center py-8">No trainings in catalog yet</p>}
            </div>
          </div>
          <div className="flex items-center gap-12 pt-8 border-t border-slate-100">
            <button type="button" onClick={onClose} className="flex-1 px-20 py-11 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 px-20 py-11 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all disabled:opacity-60 flex items-center justify-center gap-8">
              {saving ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Creating...</> : <><Icon name="Plus" size={16} />Create Path</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AssignPathModal = ({ path, partners, onClose, onSuccess }) => {
  const [partnerId, setPartnerId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!partnerId) { setError('Select a partner.'); return; }
    setSaving(true); setError(null);
    try { await learningPathsService?.assignToPartner(partnerId, path?.id); onSuccess(); }
    catch (err) { setError(err?.message || 'Failed to assign.'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-16">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        <div className="flex items-center justify-between px-24 py-18 border-b border-slate-100 bg-gradient-to-r from-emerald-50 to-teal-50">
          <div className="flex items-center gap-12">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center"><Icon name="UserPlus" size={18} className="text-white" /></div>
            <div><h2 className="text-base font-bold text-slate-900">Assign Path</h2><p className="text-xs text-slate-500 mt-1">{path?.path_name}</p></div>
          </div>
          <button onClick={onClose} className="p-8 rounded-lg hover:bg-slate-100 text-slate-500"><Icon name="X" size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-24 space-y-14">
          {error && <div className="flex items-center gap-10 p-12 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm"><Icon name="AlertCircle" size={15} />{error}</div>}
          <div><label className="block text-xs font-semibold text-slate-600 mb-6">Partner *</label>
            <select value={partnerId} onChange={e => setPartnerId(e?.target?.value)} className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 bg-white" required>
              <option value="">— Select Partner —</option>
              {partners?.map(p => <option key={p?.id} value={p?.id}>{p?.brand_name || p?.legal_name}</option>)}
            </select></div>
          <div className="flex items-center gap-12 pt-8 border-t border-slate-100">
            <button type="button" onClick={onClose} className="flex-1 px-20 py-11 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 px-20 py-11 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all disabled:opacity-60 flex items-center justify-center gap-8">
              {saving ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Assigning...</> : <><Icon name="UserPlus" size={16} />Assign</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const LearningPaths = () => {
  const toast = useToast();
  const [paths, setPaths] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTier, setFilterTier] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [assignPath, setAssignPath] = useState(null);
  const [expandedPath, setExpandedPath] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [ps, cat, pts] = await Promise.all([learningPathsService?.getAll(), trainingCatalogService?.getAll(), partnerService?.getAll()]);
      setPaths(ps || []);
      setCatalog(cat || []);
      setPartners(pts || []);
    } catch (err) { toast?.error('Failed to load learning paths.'); }
    finally { setLoading(false); }
  };

  const filtered = filterTier === 'all' ? paths : paths?.filter(p => p?.target_tier === filterTier);

  return (
    <div className="space-y-20">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-24">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-16 mb-20">
          <div className="flex items-center gap-14">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/25">
              <Icon name="Route" size={22} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">Learning Paths</h2>
              <p className="text-sm text-slate-500 mt-1">Structured training journeys per partner tier</p>
            </div>
          </div>
          <button onClick={() => setShowAddModal(true)} className="flex items-center gap-8 px-20 py-11 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all">
            <Icon name="Plus" size={16} />Create Path
          </button>
        </div>
        {/* Tier Filter */}
        <div className="flex flex-wrap gap-10">
          <button onClick={() => setFilterTier('all')} className={`px-16 py-8 rounded-xl text-sm font-semibold transition-all ${filterTier === 'all' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>All Tiers</button>
          {TIERS?.map(tier => {
            const tc = tierColors?.[tier];
            return (
              <button key={tier} onClick={() => setFilterTier(tier)} className={`px-16 py-8 rounded-xl text-sm font-semibold transition-all border ${filterTier === tier ? `bg-gradient-to-r ${tc?.gradient} text-white border-transparent shadow-md` : `${tc?.bg} ${tc?.text} ${tc?.border} hover:shadow-sm`}`}>{tier}</button>
            );
          })}
        </div>
      </div>

      {/* Paths by Tier */}
      {loading ? (
        <div className="flex items-center justify-center py-48"><div className="w-10 h-10 border-4 border-amber-500/20 border-t-amber-500 rounded-full animate-spin" /></div>
      ) : filtered?.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-48 text-center"><Icon name="Route" size={40} className="text-slate-300 mx-auto mb-16" /><p className="text-slate-500 font-medium">No learning paths found</p><button onClick={() => setShowAddModal(true)} className="mt-12 px-16 py-8 rounded-xl bg-amber-50 text-amber-700 text-sm font-semibold hover:bg-amber-100 transition-colors">Create First Path</button></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20">
          {filtered?.map(path => {
            const tc = tierColors?.[path?.target_tier] || tierColors?.Silver;
            const items = path?.learning_path_items || [];
            const isExpanded = expandedPath === path?.id;
            return (
              <div key={path?.id} className={`bg-white rounded-2xl border shadow-sm hover:shadow-md transition-all overflow-hidden ${tc?.border}`}>
                <div className={`px-24 py-18 ${tc?.bg}`}>
                  <div className="flex items-start justify-between gap-12">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-10 mb-8">
                        <span className={`px-10 py-4 rounded-lg text-xs font-bold bg-gradient-to-r ${tc?.gradient} text-white`}>{path?.target_tier}</span>
                        {path?.estimated_duration && <span className="flex items-center gap-5 text-xs text-slate-500"><Icon name="Clock" size={12} />{path?.estimated_duration}</span>}
                      </div>
                      <h3 className="text-base font-bold text-slate-900">{path?.path_name}</h3>
                      {path?.description && <p className="text-xs text-slate-500 mt-4 line-clamp-2">{path?.description}</p>}
                    </div>
                    <div className="flex items-center gap-8 flex-shrink-0">
                      <span className="text-xs font-semibold text-slate-500">{items?.length} modules</span>
                    </div>
                  </div>
                </div>
                <div className="px-24 py-16">
                  {isExpanded && items?.length > 0 && (
                    <div className="mb-16 space-y-8">
                      {items?.sort((a, b) => a?.sort_order - b?.sort_order)?.map((item, idx) => (
                        <div key={item?.id} className="flex items-center gap-12">
                          <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0 text-xs font-bold text-slate-500">{idx + 1}</div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">{item?.training_catalog?.title}</p>
                            <p className="text-xs text-slate-500">{item?.training_catalog?.category} · {item?.training_catalog?.duration}</p>
                          </div>
                          {item?.is_required && <span className="text-xs text-red-600 font-semibold">Required</span>}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-10">
                    <button onClick={() => setExpandedPath(isExpanded ? null : path?.id)} className="flex items-center gap-6 px-14 py-8 rounded-xl bg-slate-100 text-slate-600 text-xs font-semibold hover:bg-slate-200 transition-colors">
                      <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={14} />{isExpanded ? 'Hide' : 'View'} Modules
                    </button>
                    <button onClick={() => setAssignPath(path)} className="flex items-center gap-6 px-14 py-8 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-xs font-semibold hover:shadow-md transition-all">
                      <Icon name="UserPlus" size={14} />Assign to Partner
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showAddModal && <AddPathModal catalog={catalog} onClose={() => setShowAddModal(false)} onSuccess={() => { setShowAddModal(false); loadData(); toast?.success('Learning path created.'); }} />}
      {assignPath && <AssignPathModal path={assignPath} partners={partners} onClose={() => setAssignPath(null)} onSuccess={() => { setAssignPath(null); toast?.success('Learning path assigned to partner.'); }} />}
    </div>
  );
};

export default LearningPaths;
